import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { navMenu, riverStone, IMG } from "../data/brand";

const featImg = {
  STEAK_PLATED: IMG.steakPlated,
  COCKTAIL: IMG.cocktail,
  PRIVATE_DINING: IMG.privateDining,
  FIREPLACE: IMG.fireplace,
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [mobileExpand, setMobileExpand] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setHovered(null); }, [location.pathname, location.search]);

  const go = (e, to) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.slice(2);
      const scroll = () => {
        const el = document.getElementById(id);
        if (el && window.lenis) window.lenis.scrollTo(el, { offset: -80 });
        else if (el) el.scrollIntoView({ behavior: "smooth" });
      };
      if (location.pathname !== "/") { navigate("/"); setTimeout(scroll, 500); } else scroll();
      setOpen(false);
    }
  };

  const solid = scrolled || open || hovered !== null;
  const activeItem = navMenu.find((n) => n.label === hovered);

  return (
    <>
      <header data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${solid ? "bg-[#0a0908]/95 backdrop-blur-md border-b border-white/10 py-4" : "bg-gradient-to-b from-black/60 to-transparent py-6"}`}
        onMouseLeave={() => setHovered(null)}>
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 flex items-center justify-between">
          {/* Mobile-only left spacer to balance the hamburger and truly center the logo */}
          <div className="lg:hidden w-8 shrink-0" aria-hidden="true" />
          {/* Left nav */}
          <nav className="hidden lg:flex items-center gap-8 flex-1">
            <Link to="/" data-testid="nav-home"
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-white/80 hover:text-[#c5a059] transition-colors py-2">
              Home
            </Link>
            {navMenu.map((item) => (
              <div key={item.label} onMouseEnter={() => setHovered(item.label)} className="py-2">
                <Link to={item.to} onClick={(e) => go(e, item.to)} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.2em] uppercase text-white/80 hover:text-[#c5a059] transition-colors">
                  {item.label}
                  <ChevronDown size={12} className={`transition-transform ${hovered === item.label ? "rotate-180" : ""}`} />
                </Link>
              </div>
            ))}
          </nav>

          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex-1 lg:flex-none flex justify-center lg:block lg:absolute lg:left-1/2 lg:-translate-x-1/2">
            <img src={IMG.logo} alt="River Stone Chophouse" className={`w-auto transition-all duration-500 ${solid ? "h-12 md:h-14" : "h-14 md:h-16"}`} />
          </Link>

          {/* Right CTA */}
          <div className="hidden lg:flex items-center justify-end gap-6 flex-1">
            <a href={riverStone.phoneHref} className="text-[11px] tracking-[0.15em] text-white/70 hover:text-white transition-colors">{riverStone.phone}</a>
            <Link to="/reservations" data-testid="nav-reservations-btn"
              className="bg-[#C1272D] text-white hover:bg-[#9E1F24] px-7 py-3 text-[11px] font-semibold tracking-[0.2em] uppercase transition-colors duration-300">
              Reservations
            </Link>
          </div>

          {/* Mobile toggle */}
          <button className="lg:hidden text-white p-1" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Desktop MEGA menu panel */}
        <AnimatePresence>
          {activeItem && activeItem.children && (
            <motion.div
              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block absolute left-0 right-0 top-full backdrop-blur-xl border-t border-b border-white/10 shadow-2xl"
              style={{ backgroundColor: "rgba(10,9,8,0.985)" }}
              onMouseEnter={() => setHovered(activeItem.label)}
            >
              <div className="max-w-[1500px] mx-auto px-10 py-10 grid grid-cols-12 gap-10">
                {/* Feature visual */}
                {activeItem.feature && (
                  <Link to={activeItem.feature.to} onClick={(e) => go(e, activeItem.feature.to)}
                    className="col-span-4 group relative overflow-hidden aspect-[4/3]">
                    <motion.img
                      key={activeItem.label}
                      initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                      src={featImg[activeItem.feature.image]} alt={activeItem.feature.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <span className="text-[9px] tracking-[0.35em] uppercase text-[#c5a059]">{activeItem.feature.eyebrow}</span>
                      <h4 className="font-serif text-2xl text-white mt-2 leading-tight">{activeItem.feature.title}</h4>
                      <p className="text-white/60 text-xs font-light mt-2">{activeItem.feature.blurb}</p>
                    </div>
                  </Link>
                )}

                {/* Links */}
                <div className="col-span-8 grid grid-cols-2 gap-x-10 gap-y-1 content-start">
                  <p className="col-span-2 text-[10px] tracking-[0.3em] uppercase text-white/30 mb-3">{activeItem.label}</p>
                  {activeItem.children.map((c, idx) => (
                    <motion.div key={c.label}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 + idx * 0.05 }}>
                      <Link to={c.to} onClick={(e) => go(e, c.to)} data-testid={`mega-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="group block py-3 border-b border-white/5">
                        <div className="flex items-center justify-between">
                          <span className="font-serif text-xl text-white/90 group-hover:text-[#c5a059] transition-colors">{c.label}</span>
                          <ArrowUpRight size={15} className="text-white/20 group-hover:text-[#c5a059] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                        </div>
                        {c.desc && <p className="text-white/40 text-xs font-light mt-0.5">{c.desc}</p>}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 bg-[#0a0908] pt-24 px-8 lg:hidden overflow-y-auto pb-32" data-testid="mobile-menu-drawer">
            <nav className="flex flex-col">
              <Link to="/" data-testid="mobile-nav-home" className="border-b border-white/10 py-4 font-serif text-3xl text-white/90">Home</Link>
              {navMenu.map((item) => (
                <div key={item.label} className="border-b border-white/10 py-4">
                  <button onClick={() => setMobileExpand(mobileExpand === item.label ? null : item.label)}
                    className="flex items-center justify-between w-full">
                    <span className="font-serif text-3xl text-white/90">{item.label}</span>
                    <ChevronDown size={20} className={`text-[#c5a059] transition-transform ${mobileExpand === item.label ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileExpand === item.label && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden">
                        <div className="flex flex-col gap-3 pt-4 pl-1">
                          {item.children.map((c) => (
                            <Link key={c.label} to={c.to} onClick={(e) => go(e, c.to)}
                              className="text-white/60 text-sm tracking-[0.1em] uppercase hover:text-[#c5a059]">{c.label}</Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <Link to="/reservations" className="mt-8 bg-[#C1272D] text-white px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase text-center" data-testid="mobile-reservations-btn">
                Reservations
              </Link>
              <a href={riverStone.phoneHref} className="text-sm tracking-[0.15em] text-white/60 mt-4 text-center">{riverStone.phone}</a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
